/**
 * Workspace · New transfer composer (S38) — consolidated voucher-preview layout.
 *
 * Single unified card on the left + sticky live voucher document preview
 * on the right. Posts a `cash_movements` row with `kind=transfer` per
 * ADR-0012 §2. Supports `?from`/`?to` deep-link search params for entry
 * from `/cash/bank-accounts/$id` and the topbar `+ New ▾` quick action.
 *
 * @see prototype/cash/transfer-new.html — design contract (consolidated form + voucher preview)
 * @see prototype/cash/transfer-detail.html — voucher vocabulary mirrored on the right
 * @see docs/adr/0012-bank-accounts-and-cash-movements.md §2 (transfer kind) · §6 (Dr/Cr posting) · §10.7 (illegal combos)
 */
import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, ArrowLeft, ArrowRight, Check, Plus, Wallet } from "lucide-react";
import {
  Alert,
  Button,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import {
  classifySubtype,
  SUBTYPE_LABEL,
  SubtypePill,
  type BankAccountType,
  type TransferSubtype,
} from "../../../../features/transfers/index.ts";
import { paiseToInr } from "../../../../lib/format.ts";

// ── Route ──────────────────────────────────────────────────────────

interface TransferNewSearch {
  from?: string;
  to?: string;
}

export const Route = createFileRoute("/(workspace)/cash/transfers/new")({
  component: NewTransferPage,
  validateSearch: (s: Record<string, unknown>): TransferNewSearch => {
    const out: TransferNewSearch = {};
    if (typeof s.from === "string" && s.from) out.from = s.from;
    if (typeof s.to === "string" && s.to) out.to = s.to;
    return out;
  },
});

// ── Wire shapes ────────────────────────────────────────────────────

interface AccountWire {
  id: string;
  name: string;
  type: BankAccountType;
  accountNumberLast4: string | null;
  runningBalancePaise: string;
  imprestAmountPaise: string | null;
  creditLimitPaise: string | null;
  /** ISO datetime of the last `changeBankDetails` post; drives the 24h cooling-off hint. */
  bankDetailsChangedAt: string | null;
  archivedAt: string | null;
}

/** Subset of `cashMovements.post` we read. */
interface PostMovementResult {
  awaitingApproval: { summary: string } | null;
}

// ── Page ───────────────────────────────────────────────────────────

function NewTransferPage() {
  const search = Route.useSearch();
  const accountsQ = trpc.bankAccounts.list.useQuery({});
  // Approval threshold — pulled from petty-cash anchor (companies row).
  // Fallback to ₹50,000 (system-decisions §4) when the query fails.
  const anchorQ = trpc.pettyCash.anchorData.useQuery({});

  const breadcrumbs = [
    { label: "Cash" },
    { label: "Transfers", to: "/cash/transfers" as const },
    { label: "New transfer" },
  ];

  if (accountsQ.isLoading) {
    return (
      <WorkspaceShell breadcrumbs={breadcrumbs}>
        <div className="bg-paper border border-line rounded p-6">
          <p className="text-sm text-muted">Loading accounts…</p>
        </div>
      </WorkspaceShell>
    );
  }
  if (accountsQ.error || !accountsQ.data) {
    return (
      <WorkspaceShell breadcrumbs={breadcrumbs}>
        <Alert
          intent="bad"
          title="Couldn't load accounts"
          description={accountsQ.error?.message ?? "Missing data"}
        />
      </WorkspaceShell>
    );
  }

  const active = (accountsQ.data.rows as ReadonlyArray<AccountWire>).filter(
    (r) => r.archivedAt === null,
  );

  const approvalThresholdPaise = anchorQ.data?.approvalThresholdPaise
    ? BigInt(anchorQ.data.approvalThresholdPaise)
    : 5_000_000n;

  return (
    <TransferForm
      accounts={active}
      initialFrom={search.from}
      initialTo={search.to}
      approvalThresholdPaise={approvalThresholdPaise}
    />
  );
}

// ── Form ───────────────────────────────────────────────────────────

function TransferForm({
  accounts,
  initialFrom,
  initialTo,
  approvalThresholdPaise,
}: {
  accounts: ReadonlyArray<AccountWire>;
  initialFrom: string | undefined;
  initialTo: string | undefined;
  approvalThresholdPaise: bigint;
}) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  // ── Default picker state — never start in an illegal combo ────────
  // From defaults: first non-card account (cards can't be the source).
  // To defaults: the search param if valid, else the first account that
  // isn't the From — preferring a cash drawer when From is a bank so the
  // composer opens on the canonical "petty-cash top-up" path.
  const defaultFromId =
    (initialFrom && accounts.find((a) => a.id === initialFrom && a.type !== "credit_card")?.id) ??
    accounts.find((a) => a.type === "bank")?.id ??
    accounts.find((a) => a.type !== "credit_card")?.id ??
    "";
  const defaultFromAccount = accounts.find((a) => a.id === defaultFromId);
  const defaultToId =
    (initialTo && accounts.find((a) => a.id === initialTo && a.id !== defaultFromId)?.id) ??
    (defaultFromAccount?.type === "bank"
      ? accounts.find((a) => a.type === "cash" && a.id !== defaultFromId)?.id
      : undefined) ??
    accounts.find((a) => a.id !== defaultFromId)?.id ??
    "";

  const [fromAccountId, setFromAccountId] = useState<string>(defaultFromId);
  const [toAccountId, setToAccountId] = useState<string>(defaultToId);
  const [amountRupees, setAmountRupees] = useState("");
  const [valueDate, setValueDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [narration, setNarration] = useState(() =>
    seedNarration(
      accounts.find((a) => a.id === defaultFromId) ?? null,
      accounts.find((a) => a.id === defaultToId) ?? null,
    ),
  );
  const [reference, setReference] = useState("");
  const [showReference, setShowReference] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const mutation = trpc.cashMovements.post.useMutation({
    onSuccess: async (res: PostMovementResult) => {
      // Refresh movement list + balances + briefings. `pettyCash.anchorData`
      // is invalidated unconditionally because BANK→CASH transfers touch
      // the drawer balance the petty-cash anchor surfaces.
      await Promise.all([
        utils.cashMovements.list.invalidate(),
        utils.bankAccounts.list.invalidate(),
        utils.briefings.workspaceSignals.invalidate(),
        utils.pettyCash.anchorData.invalidate(),
      ]);
      if (res.awaitingApproval) {
        setInfo(
          `Queued for founder review (${res.awaitingApproval.summary}). Tracked in the approvals inbox.`,
        );
      } else {
        void navigate({ to: "/cash/transfers" });
      }
    },
    onError: (err: { message: string }) => setServerError(err.message),
  });

  // From-picker excludes credit_card type (cards can't be the source per ADR-0012 §10.7).
  const fromOptions = useMemo(() => accounts.filter((a) => a.type !== "credit_card"), [accounts]);
  // To-picker excludes the currently-selected From; cards are allowed.
  const toOptions = useMemo(
    () => accounts.filter((a) => a.id !== fromAccountId),
    [accounts, fromAccountId],
  );

  const fromAccount = accounts.find((a) => a.id === fromAccountId) ?? null;
  const toAccount = accounts.find((a) => a.id === toAccountId) ?? null;

  const subtype: TransferSubtype | null =
    fromAccount && toAccount ? classifySubtype(fromAccount.type, toAccount.type) : null;

  // Parse amount (rupees → paise) — accepts "22,798" / "22798" / "22798.50".
  const amountPaise = parseRupeesToPaise(amountRupees);

  const fromBeforePaise = fromAccount ? BigInt(fromAccount.runningBalancePaise) : 0n;
  const toBeforePaise = toAccount ? BigInt(toAccount.runningBalancePaise) : 0n;
  const fromAfterPaise = fromBeforePaise - amountPaise;
  // Card outstanding shrinks toward zero (toBefore is a positive liability magnitude).
  const toAfterPaise =
    toAccount?.type === "credit_card" ? toBeforePaise - amountPaise : toBeforePaise + amountPaise;

  // ── Guards ─────────────────────────────────────────────────────────
  // Banks may be overdrafted in V1 (real bank confirms by refusing the wire);
  // cash and card sources must always cover. We allow bank source even when
  // amountPaise > balance so the operator can still post a wire-out.
  const sourceCovers = (() => {
    if (!fromAccount) return false;
    if (amountPaise <= 0n) return true;
    if (fromAccount.type === "bank") return true;
    return amountPaise <= fromBeforePaise;
  })();

  const coolingOff =
    isWithin24h(fromAccount?.bankDetailsChangedAt ?? null) ||
    isWithin24h(toAccount?.bankDetailsChangedAt ?? null);

  const approvalNeeded = amountPaise > approvalThresholdPaise;

  // ── Validity gating for the Post button ────────────────────────────
  const valid =
    !!fromAccount &&
    !!toAccount &&
    fromAccount.id !== toAccount.id &&
    subtype !== null &&
    amountPaise > 0n &&
    sourceCovers &&
    !coolingOff &&
    narration.trim().length > 0;

  // ── Quick-fill values (rupees) per To-type ─────────────────────────
  // BANK→CASH: top-up to imprest = imprest − current balance (clamped at 0).
  // BANK→CARD: statement outstanding = card balance (a positive magnitude).
  // BANK→BANK / CASH→BANK: no quick chip beyond Custom.
  const quickfillTopUpRupees = (() => {
    if (toAccount?.type !== "cash" || !toAccount.imprestAmountPaise) return null;
    const imprest = BigInt(toAccount.imprestAmountPaise);
    const gap = imprest - toBeforePaise;
    return gap > 0n ? paiseToRupeesString(gap) : "0";
  })();
  const quickfillFullImprestRupees =
    toAccount?.type === "cash" && toAccount.imprestAmountPaise
      ? paiseToRupeesString(BigInt(toAccount.imprestAmountPaise))
      : null;
  const quickfillStatementOutstandingRupees =
    toAccount?.type === "credit_card" ? paiseToRupeesString(toBeforePaise) : null;
  // Minimum due is approximated at 10% of the outstanding — same heuristic
  // the prototype uses (statement-min ≈ 10% per HDFC corp card terms).
  const quickfillMinDueRupees =
    toAccount?.type === "credit_card" ? paiseToRupeesString(toBeforePaise / 10n) : null;

  // ── Handlers ───────────────────────────────────────────────────────
  function onFromChange(id: string) {
    setFromAccountId(id);
    // If the new From is the same as To, pick a sensible alternative for To.
    if (id === toAccountId) {
      const next = accounts.find((a) => a.id !== id);
      if (next) setToAccountId(next.id);
    }
    // Re-seed narration when it still looks like the default — never trample
    // operator-typed text.
    const from = accounts.find((a) => a.id === id) ?? null;
    const to = accounts.find((a) => a.id === toAccountId) ?? null;
    if (narration === "" || narration === seedNarration(fromAccount, toAccount)) {
      setNarration(seedNarration(from, to));
    }
  }

  function onToChange(id: string) {
    setToAccountId(id);
    const from = accounts.find((a) => a.id === fromAccountId) ?? null;
    const to = accounts.find((a) => a.id === id) ?? null;
    if (narration === "" || narration === seedNarration(fromAccount, toAccount)) {
      setNarration(seedNarration(from, to));
    }
  }

  function applyChipAmount(rupees: string) {
    setAmountRupees(rupees);
  }

  function submit(e: React.SyntheticEvent) {
    e.preventDefault();
    setServerError(null);
    setInfo(null);
    if (!valid) return;

    const composedNarration = reference.trim()
      ? `${narration.trim()} · ref: ${reference.trim()}`
      : narration.trim();

    mutation.mutate({
      kind: "transfer",
      fromAccountId,
      toAccountId,
      amountPaise: amountPaise.toString(),
      valueDate,
      narration: composedNarration,
    });
  }

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Cash" },
        { label: "Transfers", to: "/cash/transfers" },
        { label: "New transfer" },
      ]}
      viewportClassName="p-0 md:p-0"
      contentClassName="max-w-none"
    >
      <div className="flex flex-col min-h-full">
        {/* Sticky composer head */}
        <header className="sticky top-0 z-20 bg-paper border-b border-line min-h-[64px] px-6 py-3 flex items-center gap-3.5">
          <Link
            to="/cash/transfers"
            className="inline-grid place-items-center w-8 h-8 rounded-sm border border-line bg-paper text-ink-2 hover:bg-bg no-underline shrink-0"
            aria-label="Back to transfers"
          >
            <ArrowLeft size={14} strokeWidth={2} />
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="m-0 text-lg font-bold tracking-tight text-ink leading-tight flex items-center gap-2.5 flex-wrap">
              <span>New transfer</span>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100 uppercase tracking-wider">
                Transfer
              </span>
            </h1>
            <p className="mt-0.5 text-xs text-muted">Move money between own-to-own accounts.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button asChild intent="ghost" size="md">
              <Link to="/cash/transfers">Cancel</Link>
            </Button>
            <Button
              type="button"
              intent="primary"
              size="md"
              onClick={submit}
              disabled={!valid || mutation.isPending}
            >
              <Check size={12} strokeWidth={2.4} aria-hidden />
              {mutation.isPending ? "Posting…" : "Post transfer"}
            </Button>
          </div>
        </header>

        {/* 60/40 split body */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] items-start">
          {/* LEFT — single consolidated form card */}
          <main className="px-6 pt-6 pb-16 min-w-0 flex flex-col gap-3.5">
            <form
              onSubmit={submit}
              className="bg-paper border border-line rounded shadow overflow-hidden"
            >
              <div className="px-4 py-3.5 flex flex-col gap-3.5">
                {/* From */}
                <Field label="From" required>
                  <Select value={fromAccountId} onValueChange={onFromChange}>
                    <SelectTrigger id="from-account" aria-label="From account">
                      <SelectValue placeholder="Pick an account…" />
                    </SelectTrigger>
                    <SelectContent>
                      <GroupedAccountOptions options={fromOptions} />
                    </SelectContent>
                  </Select>
                </Field>

                {/* To */}
                <Field label="To" required>
                  <Select value={toAccountId} onValueChange={onToChange}>
                    <SelectTrigger id="to-account" aria-label="To account">
                      <SelectValue placeholder="Pick an account…" />
                    </SelectTrigger>
                    <SelectContent>
                      <GroupedAccountOptions options={toOptions} includeCards />
                    </SelectContent>
                  </Select>
                </Field>

                {/* Detected subtype strip */}
                <DetectedStrip subtype={subtype} fromAccount={fromAccount} toAccount={toAccount} />

                {/* Amount */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="amount-input" className="text-xs font-semibold text-ink-2">
                    Amount <span className="text-rose-600">*</span>
                  </label>
                  <div className="relative">
                    <span
                      aria-hidden
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted font-semibold text-base pointer-events-none"
                    >
                      ₹
                    </span>
                    <input
                      id="amount-input"
                      type="text"
                      inputMode="decimal"
                      value={amountRupees}
                      onChange={(e) => setAmountRupees(e.target.value)}
                      onBlur={(e) => {
                        const paise = parseRupeesToPaise(e.target.value);
                        if (paise > 0n) setAmountRupees(formatIndianRupeesFromPaise(paise));
                      }}
                      placeholder="0"
                      autoComplete="off"
                      className="w-full h-[46px] pl-8 pr-3 bg-paper border border-line rounded-sm text-ink font-mono tabular-nums font-semibold text-lg placeholder:text-muted hover:border-line-2 focus:outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100"
                      aria-label="Amount in rupees"
                    />
                  </div>
                  <AmountHelper
                    amountPaise={amountPaise}
                    sourceCovers={sourceCovers}
                    coolingOff={coolingOff}
                    approvalNeeded={approvalNeeded}
                    shortPaise={amountPaise > fromBeforePaise ? amountPaise - fromBeforePaise : 0n}
                  />

                  {/* Quick-fill chips (conditional) */}
                  <QuickFillRow
                    toAccount={toAccount}
                    topUpRupees={quickfillTopUpRupees}
                    fullImprestRupees={quickfillFullImprestRupees}
                    statementOutstandingRupees={quickfillStatementOutstandingRupees}
                    minDueRupees={quickfillMinDueRupees}
                    onPick={applyChipAmount}
                    onCustom={() => setAmountRupees("")}
                  />
                </div>

                {/* Value date + Narration grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="value-date" className="text-xs font-semibold text-ink-2">
                      Value date <span className="text-rose-600">*</span>
                    </label>
                    <input
                      id="value-date"
                      type="date"
                      value={valueDate}
                      onChange={(e) => setValueDate(e.target.value)}
                      className="w-full h-[38px] px-3 bg-paper border border-line rounded-sm text-ink placeholder:text-muted hover:border-line-2 focus:outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="narration-input" className="text-xs font-semibold text-ink-2">
                      Narration <span className="text-rose-600">*</span>
                    </label>
                    <input
                      id="narration-input"
                      type="text"
                      value={narration}
                      onChange={(e) => setNarration(e.target.value)}
                      placeholder="What this transfer is for"
                      className="w-full h-[38px] px-3 bg-paper border border-line rounded-sm text-ink placeholder:text-muted hover:border-line-2 focus:outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100"
                    />
                  </div>
                </div>

                {/* Add-reference toggle */}
                {!showReference ? (
                  <button
                    type="button"
                    onClick={() => setShowReference(true)}
                    className="self-start inline-flex items-center gap-1.5 bg-transparent border-0 p-0 text-xs font-semibold text-sky-700 hover:underline cursor-pointer"
                  >
                    <Plus size={12} strokeWidth={2.4} />
                    Add reference (optional)
                  </button>
                ) : (
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="reference-input" className="text-xs font-semibold text-ink-2">
                      Reference <span className="text-muted font-medium ml-1">(optional)</span>
                    </label>
                    <input
                      id="reference-input"
                      type="text"
                      value={reference}
                      onChange={(e) => setReference(e.target.value)}
                      placeholder="NEFT UTR / IMPS ref / cheque #"
                      autoComplete="off"
                      className="w-full h-[38px] px-3 bg-paper border border-line rounded-sm text-ink font-mono tabular-nums placeholder:text-muted hover:border-line-2 focus:outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100"
                    />
                  </div>
                )}
              </div>
            </form>

            <p className="text-xs text-muted pl-1 leading-relaxed">
              Posts as a single{" "}
              <code className="font-mono text-xs bg-ink/5 px-1.5 py-0.5 rounded text-ink-2">
                cash_movements.kind=transfer
              </code>{" "}
              row per ADR-0012 §2.
            </p>

            {serverError ? (
              <Alert intent="bad" title="Couldn't post the transfer" description={serverError} />
            ) : null}
            {info ? <Alert intent="info" title="Heads up" description={info} /> : null}
          </main>

          {/* RIGHT — sticky live voucher preview */}
          <aside
            className="lg:sticky lg:top-[64px] self-start px-5 pt-6 pb-8 border-l border-line bg-bg lg:min-h-[calc(100vh-64px)] flex flex-col gap-3"
            aria-label="Live voucher preview"
          >
            <VoucherPreview
              fromAccount={fromAccount}
              toAccount={toAccount}
              amountPaise={amountPaise}
              valueDate={valueDate}
              narration={narration}
              subtype={subtype}
            />
            <AfterBalanceCard
              fromAccount={fromAccount}
              toAccount={toAccount}
              amountPaise={amountPaise}
              fromBeforePaise={fromBeforePaise}
              toBeforePaise={toBeforePaise}
              fromAfterPaise={fromAfterPaise}
              toAfterPaise={toAfterPaise}
            />
            <GuardsCard
              sourceCovers={sourceCovers}
              coolingOff={coolingOff}
              approvalNeeded={approvalNeeded}
              shortPaise={amountPaise > fromBeforePaise ? amountPaise - fromBeforePaise : 0n}
              approvalThresholdPaise={approvalThresholdPaise}
            />
          </aside>
        </div>
      </div>
    </WorkspaceShell>
  );
}

// ── Sub-components ─────────────────────────────────────────────────

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-ink-2">
        {label}
        {required ? <span className="text-rose-600 ml-0.5">*</span> : null}
      </label>
      {children}
    </div>
  );
}

/**
 * Group account options by type — Bank / Cash / Credit card — using
 * shadcn `SelectGroup` + `SelectLabel` + `SelectItem`. `includeCards`
 * gates the third group (cards can never be a source per ADR-0012).
 */
function GroupedAccountOptions({
  options,
  includeCards = false,
}: {
  options: ReadonlyArray<AccountWire>;
  includeCards?: boolean;
}) {
  const banks = options.filter((a) => a.type === "bank");
  const cash = options.filter((a) => a.type === "cash");
  const cards = includeCards ? options.filter((a) => a.type === "credit_card") : [];
  return (
    <>
      {banks.length > 0 ? (
        <SelectGroup>
          <SelectLabel>Bank</SelectLabel>
          {banks.map((a) => (
            <SelectItem key={a.id} value={a.id}>
              {accountLineLabel(a)}
            </SelectItem>
          ))}
        </SelectGroup>
      ) : null}
      {cash.length > 0 ? (
        <SelectGroup>
          <SelectLabel>Cash</SelectLabel>
          {cash.map((a) => (
            <SelectItem key={a.id} value={a.id}>
              {accountLineLabel(a)}
            </SelectItem>
          ))}
        </SelectGroup>
      ) : null}
      {cards.length > 0 ? (
        <SelectGroup>
          <SelectLabel>Credit card</SelectLabel>
          {cards.map((a) => (
            <SelectItem key={a.id} value={a.id}>
              {accountLineLabel(a)}
            </SelectItem>
          ))}
        </SelectGroup>
      ) : null}
    </>
  );
}

function accountLineLabel(a: AccountWire): string {
  const detail =
    a.type === "cash" ? "drawer" : a.accountNumberLast4 ? `···${a.accountNumberLast4}` : null;
  const typeLabel = a.type === "credit_card" ? "Card" : a.type === "cash" ? "Cash" : "Bank";
  const balanceLabel =
    a.type === "credit_card"
      ? `${paiseToInr(a.runningBalancePaise)} outstanding`
      : paiseToInr(a.runningBalancePaise);
  return [a.name, detail, typeLabel, balanceLabel].filter(Boolean).join(" · ");
}

function DetectedStrip({
  subtype,
  fromAccount,
  toAccount,
}: {
  subtype: TransferSubtype | null;
  fromAccount: AccountWire | null;
  toAccount: AccountWire | null;
}) {
  if (!fromAccount || !toAccount) {
    return null;
  }

  // Illegal combo — card-as-source, same-account, or cash↔cash. Render in rose.
  if (subtype === null) {
    return (
      <div className="-mt-1.5 px-3 py-2.5 bg-rose-50 border border-rose-100 border-l-[3px] border-l-rose-600 rounded-sm flex items-center gap-2.5">
        <span className="text-xs font-bold uppercase tracking-wider text-muted shrink-0">
          Detected
        </span>
        <span className="font-mono text-xs font-bold tracking-wide px-2 py-0.5 rounded-sm bg-rose-100 text-rose-600">
          INVALID
        </span>
        <span className="text-xs text-rose-600 font-medium leading-snug flex-1 min-w-0">
          {invalidCopy(fromAccount, toAccount)}
        </span>
      </div>
    );
  }

  const tone = subtypeToneClasses(subtype);
  return (
    <div
      className={`-mt-1.5 px-3 py-2.5 border border-l-[3px] rounded-sm flex items-center gap-2.5 ${tone.wrapper}`}
    >
      <span className="text-xs font-bold uppercase tracking-wider text-muted shrink-0">
        Detected
      </span>
      <span
        className={`font-mono text-xs font-bold tracking-wide px-2 py-0.5 rounded-sm ${tone.pill}`}
      >
        {SUBTYPE_LABEL[subtype]}
      </span>
      <span className="text-xs text-ink-2 leading-snug flex-1 min-w-0">{subtypeCopy(subtype)}</span>
    </div>
  );
}

function subtypeCopy(s: TransferSubtype): string {
  switch (s) {
    case "bank_to_bank":
      return "Internal bank transfer · watch NEFT cutoffs";
    case "bank_to_cash":
      return "Petty cash top-up";
    case "cash_to_bank":
      return "Cash deposit to bank";
    case "bank_to_card":
      return "Credit-card statement settlement";
  }
}

function invalidCopy(from: AccountWire, to: AccountWire): string {
  if (from.id === to.id) return "From and To must be different accounts.";
  if (from.type === "credit_card")
    return "Cards cannot be the source of a transfer (ADR-0012 §10.7).";
  if (from.type === "cash" && to.type === "cash")
    return "Cash-to-cash transfers are not supported.";
  return "Pick a valid source and destination.";
}

function subtypeToneClasses(s: TransferSubtype): { wrapper: string; pill: string } {
  // Mirrors prototype's .detected.tone-* classes — emerald (default), indigo,
  // amber. The card subtype = amber, BANK→CASH = emerald, BANK→BANK and
  // CASH→BANK = indigo.
  switch (s) {
    case "bank_to_cash":
      return {
        wrapper: "bg-ok-50 border-ok-100 border-l-ok",
        pill: "bg-ok-100 text-ok",
      };
    case "bank_to_card":
      return {
        wrapper: "bg-warn-50 border-warn-100 border-l-warn",
        pill: "bg-warn-100 text-warn",
      };
    case "bank_to_bank":
    case "cash_to_bank":
      return {
        wrapper: "bg-sky-50 border-sky-100 border-l-sky-600",
        pill: "bg-sky-100 text-sky-700",
      };
  }
}

function AmountHelper({
  amountPaise,
  sourceCovers,
  coolingOff,
  approvalNeeded,
  shortPaise,
}: {
  amountPaise: bigint;
  sourceCovers: boolean;
  coolingOff: boolean;
  approvalNeeded: boolean;
  shortPaise: bigint;
}) {
  if (amountPaise <= 0n) {
    return (
      <p className="text-xs text-muted leading-snug">
        Enter how much moved between the two accounts.
      </p>
    );
  }
  if (!sourceCovers) {
    return (
      <p className="inline-flex items-center gap-1 text-xs text-warn font-medium leading-snug">
        <AlertTriangle size={11} strokeWidth={2.4} aria-hidden />
        Source short by {paiseToInr(shortPaise)}
      </p>
    );
  }
  if (coolingOff) {
    return (
      <p className="inline-flex items-center gap-1 text-xs text-warn font-medium leading-snug">
        <AlertTriangle size={11} strokeWidth={2.4} aria-hidden />
        Bank detail changed in last 24h
      </p>
    );
  }
  if (approvalNeeded) {
    return (
      <p className="inline-flex items-center gap-1 text-xs text-warn font-medium leading-snug">
        <AlertTriangle size={11} strokeWidth={2.4} aria-hidden />
        Source covers it · Will queue for founder review
      </p>
    );
  }
  return (
    <p className="inline-flex items-center gap-1 text-xs text-ok font-medium leading-snug">
      <Check size={11} strokeWidth={2.4} aria-hidden />
      Source covers it · No cooling-off
    </p>
  );
}

function QuickFillRow({
  toAccount,
  topUpRupees,
  fullImprestRupees,
  statementOutstandingRupees,
  minDueRupees,
  onPick,
  onCustom,
}: {
  toAccount: AccountWire | null;
  topUpRupees: string | null;
  fullImprestRupees: string | null;
  statementOutstandingRupees: string | null;
  minDueRupees: string | null;
  onPick: (rupees: string) => void;
  onCustom: () => void;
}) {
  if (!toAccount) return null;

  // Cash variant
  if (toAccount.type === "cash") {
    return (
      <div className="flex flex-wrap gap-1.5 mt-2">
        {topUpRupees ? (
          <QuickFillChip
            recommended
            onClick={() => onPick(topUpRupees)}
            label={
              <>
                <Check size={11} strokeWidth={2.4} aria-hidden className="mr-1" />
                Top up to imprest ·{" "}
                <span className="font-mono font-semibold">₹{formatIndian(topUpRupees)}</span>
              </>
            }
          />
        ) : null}
        {fullImprestRupees ? (
          <QuickFillChip
            onClick={() => onPick(fullImprestRupees)}
            label={
              <>
                Full imprest ·{" "}
                <span className="font-mono font-semibold">₹{formatIndian(fullImprestRupees)}</span>
              </>
            }
          />
        ) : null}
        <QuickFillChip onClick={onCustom} label="Custom" />
      </div>
    );
  }

  // Card variant
  if (toAccount.type === "credit_card") {
    return (
      <div className="flex flex-wrap gap-1.5 mt-2">
        {statementOutstandingRupees ? (
          <QuickFillChip
            recommended
            onClick={() => onPick(statementOutstandingRupees)}
            label={
              <>
                <Check size={11} strokeWidth={2.4} aria-hidden className="mr-1" />
                Statement outstanding ·{" "}
                <span className="font-mono font-semibold">
                  ₹{formatIndian(statementOutstandingRupees)}
                </span>
              </>
            }
          />
        ) : null}
        {minDueRupees ? (
          <QuickFillChip
            onClick={() => onPick(minDueRupees)}
            label={
              <>
                Minimum due ·{" "}
                <span className="font-mono font-semibold">₹{formatIndian(minDueRupees)}</span>
              </>
            }
          />
        ) : null}
        <QuickFillChip onClick={onCustom} label="Custom" />
      </div>
    );
  }

  // Bank variant — Custom only.
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      <QuickFillChip onClick={onCustom} label="Custom" />
    </div>
  );
}

function QuickFillChip({
  recommended,
  onClick,
  label,
}: {
  recommended?: boolean;
  onClick: () => void;
  label: React.ReactNode;
}) {
  const cls = recommended
    ? "bg-ok-50 border-ok-100 text-ok hover:bg-ok-100 hover:border-ok font-semibold"
    : "bg-paper border-line text-ink-2 hover:bg-bg hover:border-line-2 hover:text-ink font-medium";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-[30px] px-3 border rounded-full text-xs inline-flex items-center gap-1 transition-colors cursor-pointer ${cls}`}
    >
      {label}
    </button>
  );
}

// ── Voucher preview (right pane) ───────────────────────────────────

function VoucherPreview({
  fromAccount,
  toAccount,
  amountPaise,
  valueDate,
  narration,
  subtype,
}: {
  fromAccount: AccountWire | null;
  toAccount: AccountWire | null;
  amountPaise: bigint;
  valueDate: string;
  narration: string;
  subtype: TransferSubtype | null;
}) {
  return (
    <article
      className="bg-paper border border-line rounded shadow-card overflow-hidden flex flex-col"
      aria-label="Live voucher preview"
    >
      {/* Voucher bar */}
      <div className="flex items-center gap-2 px-3.5 py-2 border-b border-line bg-raised shrink-0">
        <span
          aria-hidden
          className="w-1.5 h-1.5 rounded-full bg-ok ring-2 ring-ok-50 animate-pulse shrink-0"
        />
        <span className="text-xs font-semibold text-ink-2">
          Live preview <span className="text-muted font-medium ml-1">· voucher</span>
        </span>
        <span className="flex-1" />
        <span className="inline-flex items-center gap-1 h-5 px-2 rounded-full bg-warn-50 border border-warn-100 text-warn text-xs font-bold uppercase tracking-wider">
          Draft · pending post
        </span>
      </div>

      {/* Paper */}
      <div className="px-6 pt-5 pb-5 bg-gradient-to-b from-paper via-paper to-raised">
        {/* Letterhead */}
        <header className="grid grid-cols-[minmax(0,1fr)_auto] gap-3.5 items-start pb-3.5 border-b-2 border-ink">
          <div className="min-w-0">
            <div className="w-8 h-8 rounded-sm bg-sky-50 text-sky-700 border border-sky-100 grid place-items-center mb-1.5">
              <Wallet size={16} strokeWidth={2} />
            </div>
            <h2 className="m-0 text-sm font-bold text-ink tracking-tight">Sharma Industries</h2>
            <p className="mt-0.5 text-xs text-muted leading-snug">Mumbai · Internal voucher</p>
          </div>
          <div className="text-right font-mono min-w-0">
            <div className="text-xs font-bold tracking-[0.18em] text-muted mb-1 uppercase font-sans">
              Transfer Voucher
            </div>
            <h1 className="m-0 text-lg font-extrabold tracking-tight text-ink leading-none">
              {monthLabel(valueDate)}
            </h1>
          </div>
        </header>

        {/* Voucher number row */}
        <div className="mt-3 flex items-baseline gap-2 flex-wrap pb-3 border-b border-line">
          <span className="text-xs font-bold uppercase tracking-wider text-muted">Voucher #</span>
          <span className="font-mono text-xs font-semibold px-2 py-0.5 rounded-sm bg-raised border border-line text-muted">
            TRN-{valueDate || "YYYY-MM-DD"}-PENDING
          </span>
          <span className="text-xs text-muted italic">assigns on post</span>
        </div>

        {/* From / To meta */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-3.5 border-b border-line">
          <PartyCell direction="from" account={fromAccount} />
          <PartyCell direction="to" account={toAccount} />
        </div>

        {/* Single-line items table */}
        <DocItemsRow
          narration={narration}
          valueDate={valueDate}
          amountPaise={amountPaise}
          subtype={subtype}
        />

        {/* Totals strip */}
        <div className="pt-3 border-t border-line mt-0.5">
          <div className="flex justify-between items-baseline px-1 py-1 text-xs text-muted">
            <span>Amount</span>
            <span className="font-mono tabular-nums text-ink-2 font-semibold">
              {amountPaise > 0n ? `${formatIndianFromPaise(amountPaise)}.00` : "0.00"}
            </span>
          </div>
          <div className="flex justify-between items-baseline mt-1 pt-2 px-1 border-t border-line text-xs font-bold text-ink">
            <span>Total transferred</span>
            <span className="font-mono tabular-nums text-base font-extrabold text-ink">
              {amountPaise > 0n ? paiseToInr(amountPaise) : "₹0"}
            </span>
          </div>
        </div>

        {/* Posting preview footer */}
        <div className="pt-3 mt-3 border-t border-line text-xs text-muted leading-relaxed">
          <div className="flex flex-col gap-0.5 font-mono">
            <div className="grid grid-cols-[20px_minmax(0,1fr)_auto] items-baseline gap-1.5 text-ink-2">
              <span className="font-bold text-ok">Dr</span>
              <span className="text-ink-2 overflow-hidden text-ellipsis whitespace-nowrap">
                {glName(toAccount)}
              </span>
              <span className="text-ink font-semibold">{paiseToInr(amountPaise)}</span>
            </div>
            <div className="grid grid-cols-[20px_minmax(0,1fr)_auto] items-baseline gap-1.5 text-ink-2">
              <span className="font-bold text-rose-600">Cr</span>
              <span className="text-ink-2 overflow-hidden text-ellipsis whitespace-nowrap">
                {glName(fromAccount)}
              </span>
              <span className="text-ink font-semibold">{paiseToInr(amountPaise)}</span>
            </div>
          </div>
          <p className="mt-2 font-sans text-xs text-muted">
            Per{" "}
            <code className="font-mono text-xs bg-ink/5 px-1 rounded text-ink-2">ADR-0012 §6</code>
            . Posts as a single balanced Dr/Cr pair — no GL net impact.
          </p>
        </div>
      </div>
    </article>
  );
}

function PartyCell({
  direction,
  account,
}: {
  direction: "from" | "to";
  account: AccountWire | null;
}) {
  const Icon = direction === "from" ? ArrowLeft : ArrowRight;
  return (
    <div className="min-w-0">
      <div className="text-xs font-bold uppercase tracking-wider text-muted mb-1 flex items-center gap-1">
        <Icon size={10} strokeWidth={2.4} aria-hidden />
        {direction === "from" ? "From" : "To"}
      </div>
      {account ? (
        <>
          <div className="text-xs font-bold text-ink tracking-tight leading-snug flex items-center gap-1.5 flex-wrap mb-1">
            <span className="truncate max-w-full">{voucherCellName(account)}</span>
            <TypeChip type={account.type} />
          </div>
          <PartySubLine account={account} />
        </>
      ) : (
        <div className="text-xs text-muted">—</div>
      )}
    </div>
  );
}

function voucherCellName(a: AccountWire): string {
  if (a.type === "cash") return `${a.name} · drawer`;
  if (a.accountNumberLast4) return `${a.name} · ···${a.accountNumberLast4}`;
  return a.name;
}

function TypeChip({ type }: { type: BankAccountType }) {
  const cls =
    type === "bank"
      ? "bg-sky-50 text-sky-700 border-sky-100"
      : type === "cash"
        ? "bg-ok-50 text-ok border-ok-100"
        : "bg-warn-50 text-warn border-warn-100";
  const label = type === "credit_card" ? "card" : type;
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider border font-sans ${cls}`}
    >
      {label}
    </span>
  );
}

function PartySubLine({ account }: { account: AccountWire }) {
  if (account.type === "bank") {
    return (
      <p className="text-xs text-muted leading-relaxed">
        {account.accountNumberLast4 ? (
          <>
            <strong className="text-ink-2 font-medium">Account</strong>{" "}
            <span className="font-mono text-ink-2">···{account.accountNumberLast4}</span>
            <br />
          </>
        ) : null}
        Holder: <strong className="text-ink-2 font-medium">Sharma Industries Pvt Ltd</strong>
      </p>
    );
  }
  if (account.type === "cash") {
    return (
      <p className="text-xs text-muted leading-relaxed">
        {account.imprestAmountPaise ? (
          <>
            Imprest{" "}
            <span className="font-mono text-ink-2">{paiseToInr(account.imprestAmountPaise)}</span>
            <br />
          </>
        ) : null}
        Drawer custodian recorded on post
      </p>
    );
  }
  // credit_card
  return (
    <p className="text-xs text-muted leading-relaxed">
      Outstanding{" "}
      <span className="font-mono text-ink-2">{paiseToInr(account.runningBalancePaise)}</span>
      {account.creditLimitPaise ? (
        <>
          {" "}
          · Credit limit{" "}
          <span className="font-mono text-ink-2">{paiseToInr(account.creditLimitPaise)}</span>
        </>
      ) : null}
    </p>
  );
}

function DocItemsRow({
  narration,
  valueDate,
  amountPaise,
  subtype,
}: {
  narration: string;
  valueDate: string;
  amountPaise: bigint;
  subtype: TransferSubtype | null;
}) {
  return (
    <table className="w-full border-collapse mt-3 text-xs" aria-label="Transfer line">
      <thead>
        <tr>
          <th
            className="text-left py-1.5 pr-1 pl-0 text-xs font-bold uppercase tracking-wider text-muted border-b border-line"
            style={{ width: "52%" }}
          >
            Description
          </th>
          <th className="text-left py-1.5 px-1 text-xs font-bold uppercase tracking-wider text-muted border-b border-line">
            Subtype
          </th>
          <th className="text-right py-1.5 pl-1 pr-0 text-xs font-bold uppercase tracking-wider text-muted border-b border-line">
            Amount
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="py-3 pr-1 pl-0 align-middle text-ink-2">
            <strong className="block text-xs font-semibold text-ink leading-snug">
              {narration.trim() || "—"}
            </strong>
            <span className="block text-xs text-muted mt-0.5">
              Value date · {valueDate ? prettyDate(valueDate) : "—"}
            </span>
          </td>
          <td className="py-3 px-1 align-middle whitespace-nowrap">
            {subtype ? (
              <SubtypePill subtype={subtype} />
            ) : (
              <span className="text-xs text-muted">—</span>
            )}
          </td>
          <td className="py-3 pl-1 pr-0 text-right font-mono tabular-nums text-ink font-bold text-sm">
            {amountPaise > 0n ? paiseToInr(amountPaise) : "—"}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function AfterBalanceCard({
  fromAccount,
  toAccount,
  amountPaise,
  fromBeforePaise,
  toBeforePaise,
  fromAfterPaise,
  toAfterPaise,
}: {
  fromAccount: AccountWire | null;
  toAccount: AccountWire | null;
  amountPaise: bigint;
  fromBeforePaise: bigint;
  toBeforePaise: bigint;
  fromAfterPaise: bigint;
  toAfterPaise: bigint;
}) {
  return (
    <div className="bg-paper border border-line rounded shadow overflow-hidden">
      <div className="px-3.5 py-2 border-b border-line flex items-center justify-between gap-2 bg-raised">
        <h4 className="m-0 text-xs font-bold uppercase tracking-wider text-ink-2">
          After this transfer
        </h4>
        <span className="text-xs text-muted font-mono">projected</span>
      </div>
      <div className="px-3.5 py-3 flex flex-col gap-3">
        <AfterRow
          account={fromAccount}
          beforePaise={fromBeforePaise}
          afterPaise={fromAfterPaise}
          amountPaise={amountPaise}
          direction="from"
        />
        <AfterRow
          account={toAccount}
          beforePaise={toBeforePaise}
          afterPaise={toAfterPaise}
          amountPaise={amountPaise}
          direction="to"
        />
      </div>
    </div>
  );
}

function AfterRow({
  account,
  beforePaise,
  afterPaise,
  amountPaise,
  direction,
}: {
  account: AccountWire | null;
  beforePaise: bigint;
  afterPaise: bigint;
  amountPaise: bigint;
  direction: "from" | "to";
}) {
  if (!account) {
    return <div className="text-xs text-muted">—</div>;
  }
  const isCardDest = direction === "to" && account.type === "credit_card";
  // For card destinations, the outstanding shrinks — render as "₹X out → ₹Y out".
  const beforeLabel = isCardDest ? `${paiseToInr(beforePaise)} out` : paiseToInr(beforePaise);
  const afterLabel = isCardDest
    ? afterPaise <= 0n
      ? "₹0 (cleared)"
      : `${paiseToInr(afterPaise > 0n ? afterPaise : -afterPaise)} out`
    : paiseToInr(afterPaise);

  // Delta tone: source decreases (rose), destination increases (emerald). Card
  // destination's outstanding decrease also reads as "good" — emerald.
  const isUp = direction === "to";
  const deltaText = amountPaise > 0n ? `${isUp ? "+" : "−"}${paiseToInr(amountPaise)}` : "₹0";

  // Imprest fill — only shown for to-cash with imprest target.
  const imprestPct = (() => {
    if (direction !== "to" || account.type !== "cash" || !account.imprestAmountPaise) return null;
    const imprest = BigInt(account.imprestAmountPaise);
    if (imprest <= 0n) return null;
    if (afterPaise <= 0n) return 0;
    return clamp(Number((afterPaise * 100n) / imprest), 0, 100);
  })();
  const imprestOver =
    direction === "to" &&
    account.type === "cash" &&
    account.imprestAmountPaise &&
    afterPaise > BigInt(account.imprestAmountPaise);

  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5 text-xs text-ink-2 mb-1">
        <strong className="font-semibold text-ink overflow-hidden text-ellipsis whitespace-nowrap min-w-0 flex-1">
          {shortAccountName(account)}
        </strong>
        <TypePill type={account.type} />
      </div>
      <div className="flex items-baseline gap-1.5 flex-wrap font-mono tabular-nums text-xs">
        <span className="text-muted">{beforeLabel}</span>
        <span className="text-line-2 text-xs">→</span>
        <span className="text-ink font-bold">{afterLabel}</span>
        <span
          className={`ml-auto text-xs font-bold px-1.5 py-0.5 rounded-full ${
            isUp ? "bg-ok-50 text-ok" : "bg-rose-50 text-rose-600"
          }`}
        >
          {deltaText}
        </span>
      </div>
      {imprestPct !== null ? (
        <div className="mt-1.5 h-1 bg-line rounded-full overflow-hidden relative">
          <div
            className={`absolute left-0 top-0 bottom-0 rounded-full transition-all ${
              imprestOver ? "bg-warn" : "bg-ok"
            }`}
            style={{ width: `${imprestPct}%` }}
          />
        </div>
      ) : null}
    </div>
  );
}

function shortAccountName(a: AccountWire): string {
  if (a.type === "cash") return `${a.name} · drawer`;
  if (a.accountNumberLast4) return `${a.name} ···${a.accountNumberLast4}`;
  return a.name;
}

function TypePill({ type }: { type: BankAccountType }) {
  const cls =
    type === "bank"
      ? "bg-sky-50 text-sky-700 border-sky-100"
      : type === "cash"
        ? "bg-ok-50 text-ok border-ok-100"
        : "bg-warn-50 text-warn border-warn-100";
  return (
    <span
      className={`shrink-0 font-mono text-xs font-bold px-1.5 py-0.5 rounded uppercase tracking-wider border ${cls}`}
    >
      {type === "credit_card" ? "CARD" : type === "cash" ? "CASH" : "BANK"}
    </span>
  );
}

function GuardsCard({
  sourceCovers,
  coolingOff,
  approvalNeeded,
  shortPaise,
  approvalThresholdPaise,
}: {
  sourceCovers: boolean;
  coolingOff: boolean;
  approvalNeeded: boolean;
  shortPaise: bigint;
  approvalThresholdPaise: bigint;
}) {
  return (
    <div className="bg-paper border border-line rounded shadow overflow-hidden">
      <div className="px-3.5 py-2 border-b border-line flex items-center justify-between gap-2 bg-raised">
        <h4 className="m-0 text-xs font-bold uppercase tracking-wider text-ink-2">Guards</h4>
        <span className="text-xs text-muted font-mono">3 checks</span>
      </div>
      <div className="px-3.5 py-3 flex flex-col gap-1">
        <GuardRow
          status={sourceCovers ? "ok" : "warn"}
          copy={sourceCovers ? "Source covers it" : `Source short by ${paiseToInr(shortPaise)}`}
        />
        <GuardRow
          status={coolingOff ? "warn" : "ok"}
          copy={coolingOff ? "Bank detail changed in last 24h" : "No cooling-off"}
        />
        <GuardRow
          status={approvalNeeded ? "warn" : "info"}
          copy={
            approvalNeeded
              ? `Will queue for founder review (> ${paiseToInr(approvalThresholdPaise)} threshold)`
              : "Approval not required"
          }
        />
      </div>
    </div>
  );
}

function GuardRow({ status, copy }: { status: "ok" | "warn" | "info"; copy: string }) {
  const cls =
    status === "ok"
      ? "bg-ok-50 border-ok-100 text-ok"
      : status === "warn"
        ? "bg-warn-50 border-warn-100 text-warn"
        : "bg-paper border-line text-ink-2";
  const Icon = status === "warn" ? AlertTriangle : Check;
  return (
    <div
      className={`flex items-center gap-2 px-2.5 py-1.5 border rounded-sm text-xs leading-snug ${cls}`}
    >
      <span aria-hidden className="w-3 h-3 shrink-0 grid place-items-center">
        <Icon size={11} strokeWidth={2.6} />
      </span>
      <span className="flex-1 min-w-0">{copy}</span>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────

/** Parse a rupee string (commas + decimal allowed) into integer paise. */
function parseRupeesToPaise(s: string): bigint {
  const cleaned = s.replace(/[₹,\s]/g, "").trim();
  if (!cleaned) return 0n;
  if (!/^\d*(\.\d{0,2})?$/.test(cleaned)) return 0n;
  const [rupees = "0", paise = ""] = cleaned.split(".");
  return BigInt(rupees || "0") * 100n + BigInt((paise + "00").slice(0, 2) || "0");
}

/** Format a paise bigint as a plain decimal-rupees string (no commas). */
function paiseToRupeesString(paise: bigint): string {
  const abs = paise < 0n ? -paise : paise;
  const rupees = abs / 100n;
  const remainder = abs % 100n;
  const sign = paise < 0n ? "-" : "";
  return remainder === 0n
    ? `${sign}${rupees.toString()}`
    : `${sign}${rupees.toString()}.${remainder.toString().padStart(2, "0")}`;
}

/** Format an Indian-styled rupees-with-commas string from paise (e.g. 22,798). */
function formatIndianFromPaise(paise: bigint): string {
  const rupees = paise / 100n;
  return formatIndian(rupees.toString());
}

function formatIndianRupeesFromPaise(paise: bigint): string {
  return formatIndianFromPaise(paise);
}

/** Indian comma grouping (last 3 then groups of 2). */
function formatIndian(input: string): string {
  const cleaned = input.replace(/[^\d]/g, "");
  if (cleaned.length <= 3) return cleaned;
  const last3 = cleaned.slice(-3);
  const rest = cleaned.slice(0, -3);
  return rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + last3;
}

const MONTHS_UC = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
] as const;
const MONTHS_TC = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

function monthLabel(isoDate: string): string {
  if (!isoDate) return "MAY 2026";
  const [y, mStr] = isoDate.split("-");
  const m = parseInt(mStr ?? "", 10);
  if (!y || !m || m < 1 || m > 12) return "MAY 2026";
  return `${MONTHS_UC[m - 1]} ${y}`;
}

function prettyDate(isoDate: string): string {
  const [y, mStr, dStr] = isoDate.split("-");
  const m = parseInt(mStr ?? "", 10);
  const d = parseInt(dStr ?? "", 10);
  if (!y || !m || !d) return isoDate;
  return `${String(d).padStart(2, "0")} ${MONTHS_TC[m - 1]} ${y}`;
}

function glName(a: AccountWire | null): string {
  if (!a) return "—";
  if (a.type === "cash") return "Cash on hand";
  if (a.type === "credit_card")
    return `Card ${a.name}${a.accountNumberLast4 ? ` ···${a.accountNumberLast4}` : ""}`;
  return `Bank ${a.name}${a.accountNumberLast4 ? ` ···${a.accountNumberLast4}` : ""}`;
}

function seedNarration(from: AccountWire | null, to: AccountWire | null): string {
  if (!from || !to) return "";
  if (from.type === "bank" && to.type === "cash") return `Petty cash top-up — ${to.name}`;
  if (from.type === "bank" && to.type === "credit_card") return `Card payment — ${to.name}`;
  if (from.type === "cash" && to.type === "bank") return `Cash deposit — ${to.name}`;
  if (from.type === "bank" && to.type === "bank") return `Internal transfer — ${to.name}`;
  return "";
}

const COOLING_OFF_MS = 24 * 60 * 60 * 1000;

function isWithin24h(iso: string | null): boolean {
  if (!iso) return false;
  const stamp = Date.parse(iso);
  if (Number.isNaN(stamp)) return false;
  return Date.now() - stamp < COOLING_OFF_MS;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}
